import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Form } from '@unform/web';
import { parse, format } from 'date-fns';
import { isEmpty, isArray } from 'lodash';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Autocomplete, DateTime } from '~/components/Form';
import { PesquisaTabela } from '~/components/Form/v2';
import { t, validCharacters } from '~/helpers';
import { compareIsAfterDates } from '~/helpers/dateTime';
import api from '~/services/api';

import { Container } from './styles';

export default function EdicaoInicioPitStop({
  data,
  reference,
  close,
  cancelarAcao,
  setIsValidating,
}) {
  const { client } = useSelector((state) => state.user.profile);
  const inputRef = React.useRef(null);
  const [veiculo, setVeiculo] = React.useState([]);
  const [initials, setInitials] = React.useState({});
  const params = {};

  async function handleSubmit(param) {
    const formidveiculo = document.getElementById('idveiculo');
    if (formidveiculo.value) {
      if (veiculo && veiculo.idveiculo) {
        if (formidveiculo.value !== veiculo.idveiculo) {
          setVeiculo(null);
          setIsValidating(true);
          return;
        }
      } else {
        setIsValidating(true);
        return;
      }
    }
    try {
      const schema = Yup.object().shape({
        codigoatendimento: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        idveiculo: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        dataencosteatendimento: Yup.mixed()
          .transform((value) =>
            value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
          )
          .required(t('s.geral.validacao.campoobrigatorio'))
          .test(
            'dataencosteatendimento',
            t('s.cadastro.pitstop.datahoraatendimentodevesersersuperiorturno'),
            () =>
              compareIsAfterDates({
                compare: param.datahorainicialturno,
                current: param.dataencosteatendimento,
              })
          ),
        trematendimento: Yup.string()
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
          .nullable()
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
      });
      await schema.validate(param, {
        abortEarly: false,
      });
      const objSend = {
        ...initials,
        ...param,
        idempresaveiculo: veiculo.idempresaveiculo,
        idcliente: client,
      };
      await api.put('pitstop', objSend);
      await close();
      toast.success(t('s.geral.sucessoalteracao'));
    } catch (err) {
      if (err && isArray(err)) {
        toast.error(err[0]);
      }
      setIsValidating(false);
      await cancelarAcao();
      const validationErrors = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });
        reference.current.setErrors(validationErrors);
      }
    }
  }

  const cellsVehicle = useMemo(
    () => [
      {
        id: 'idveiculo',
        label: t('s.configuracao.comboio.idveiculo'),
        filter: { type: 'text' },
      },
      {
        id: 'nomemodeloveiculo',
        label: t('s.menu.cadastro.veiculo.modelo'),
        filter: { type: 'text' },
      },
    ],
    []
  );

  useEffect(() => {
    if (veiculo) {
      setIsValidating(isEmpty(veiculo.idempresaveiculo));
    } else {
      setIsValidating(true);
    }
  }, [veiculo]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    async function handleCarregaAtendimento(idatendimento) {
      const { data } = await api.get(`/pitstop/${idatendimento}`);
      setInitials(data);
      reference.current.setData(data);
    }
    if (data != null) {
      handleCarregaAtendimento(data.idatendimento);
    }
  }, [data]);

  return (
    <>
      {isEmpty(initials) ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Container>
            <Autocomplete
              name="idposto"
              label="codigonomeposto"
              url="postoscomboios"
              unique="idposto"
              title={t('s.geral.postocomboio')}
              disabled
              variant="outlined"
            />
            <DateTime
              label={t('s.menu.operacao.turno')}
              name="datahorainicialturno"
              serialize={(value) =>
                value.set({ second: 0 }).format('DD/MM/YYYY HH:mm:ss')
              }
              time
              disabled
            />
            <Input
              name="codigoatendimento"
              label={t('s.cadastro.pitstop.codigoatendimento')}
              inputRef={inputRef}
              type="number"
              style={{ marginTop: 16 }}
            />
            <DateTime
              label={t('s.cadastro.pitstop.dataencosteatendimento')}
              name="dataencosteatendimento"
              serialize={(value) =>
                value.set({ second: 0 }).format('DD/MM/YYYY HH:mm:ss')
              }
              time
            />
            <div style={{ marginTop: 16 }}>
              <PesquisaTabela
                id="idveiculo"
                name="idveiculo"
                label={t('s.menu.cadastro.veiculo')}
                primary="idveiculo"
                secondary="nomemodeloveiculo"
                url="veiculos/locomotivas"
                header={cellsVehicle}
                getValue={(value) => {
                  if (value && value?.idveiculo) {
                    setVeiculo(value);
                  } else {
                    setVeiculo(null);
                  }
                }}
              />
              <Input
                name="trematendimento"
                label={t('s.cadastro.pitstop.trem')}
                // inputRef={inputRef}
                type="text"
                style={{ marginTop: 16 }}
              />
            </div>
          </Container>
        </Form>
      )}
    </>
  );
}
